import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { CheckCircle2, FileSpreadsheet, ArrowRight } from 'lucide-react'

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'Why use the combined dataset instead of pulling directly from clicks and conversions?',
    options: [
      'The combined dataset is smaller',
      'It is already joined — one formula gets everything',
      'Google Sheets requires it',
      'VLOOKUP only works on combined data',
    ],
    correctIndex: 1,
    explanation:
      'The combined dataset already has clicks and conversions joined together. You only need one FILTER or QUERY to extract a complete report, rather than building multiple lookups from separate sheets.',
  },
  {
    question: 'Which is better for selecting specific columns: FILTER or QUERY?',
    options: [
      'FILTER — it has a column selector',
      'QUERY — FILTER returns all columns',
      'They are identical in this regard',
      'Neither can select specific columns',
    ],
    correctIndex: 1,
    explanation:
      'FILTER returns all columns from the range. QUERY lets you SELECT specific columns, making it better when you want to exclude columns like Offer ID from a single-offer report.',
  },
  {
    question: 'What should you add to make a report client-ready?',
    options: [
      'More formulas',
      'Percentage formatting, clear headers, totals',
      'Cell colours on every row',
      'A separate sheet for each day',
    ],
    correctIndex: 1,
    explanation:
      'Client-ready reports need clear formatting: percentage symbols on CR%, descriptive header labels, a totals row at the bottom, and chronological sorting so the client can read it easily.',
  },
  {
    question: 'Why do we sort by date?',
    options: [
      'Google Sheets requires it for FILTER to work',
      'So the data loads faster',
      'So the client can read the report chronologically',
      'To prevent duplicate rows',
    ],
    correctIndex: 2,
    explanation:
      'Sorting by date makes the report read naturally from earliest to latest, which is what clients expect when reviewing performance over time.',
  },
  {
    question: 'Why should the Offer ID column be excluded from a single-offer client report?',
    options: [
      'It causes errors in the formulas',
      'It is redundant — every row has the same offer, so it adds no value',
      'Google Sheets hides it automatically',
      'Clients cannot see Offer ID columns',
    ],
    correctIndex: 1,
    explanation:
      'When a report is filtered to one offer, every row has the same Offer ID. Showing it wastes space and clutters the report. The client already knows which offer the report is for.',
  },
  {
    question: 'How should you calculate the total Conversion Rate in the totals row?',
    options: [
      'Average the CR% values from each row',
      'Divide total conversions by total clicks',
      'Add up all the CR% values',
      'Use the CR% from the last row',
    ],
    correctIndex: 1,
    explanation:
      'Total CR% should be total conversions divided by total clicks. Averaging the individual CR% values would give a misleading result because each day has a different number of clicks.',
  },
  {
    question: 'What QUERY clause lets you filter by date range?',
    options: [
      'WHERE A BETWEEN date1 AND date2',
      'WHERE A >= date1 AND A <= date2',
      'DATERANGE(A, date1, date2)',
      'FILTER BY DATE(A, date1, date2)',
    ],
    correctIndex: 1,
    explanation:
      'QUERY uses standard comparison operators for date filtering. WHERE A >= date\'2024-01-15\' AND A <= date\'2024-01-31\' returns rows within that date range.',
  },
  {
    question: 'What is the benefit of using cell references for the offer ID in your QUERY or FILTER?',
    options: [
      'It makes the formula shorter',
      'You can change which offer the report shows without editing the formula',
      'It prevents errors in the formula',
      'Google Sheets requires cell references',
    ],
    correctIndex: 1,
    explanation:
      'Referencing a cell (like WHERE B=\'\'&D1&\'\') means you can type a different offer ID in cell D1 to instantly switch which offer the report shows. No formula editing needed.',
  },
  {
    question: 'A client asks why their CR% shows "0.038" instead of "3.8%". What is the fix?',
    options: [
      'Recalculate the formula',
      'Format the cell as Percentage using Format > Number > Percent',
      'Multiply the value by 100 in the formula',
      'Delete the cell and re-enter the value',
    ],
    correctIndex: 1,
    explanation:
      'Google Sheets displays raw decimal values by default. Applying percentage formatting (Format > Number > Percent) converts 0.038 to "3.8%" without changing the underlying value.',
  },
  {
    question: 'What is the purpose of adding start and end date cells that the report formula references?',
    options: [
      'To make the spreadsheet load faster',
      'To let you change the reporting period without editing formulas',
      'To prevent the report from showing future dates',
      'Google Sheets requires date parameters for FILTER',
    ],
    correctIndex: 1,
    explanation:
      'Date reference cells let you adjust the reporting period (e.g., last 7 days, this month, custom range) by simply changing the dates in those cells. The FILTER or QUERY formula picks up the new dates automatically.',
  },
  {
    question: 'Which formula correctly extracts offer_123 data sorted by date from a combined dataset?',
    options: [
      '=FILTER(A:E, B:B="offer_123", ORDER BY A)',
      '=QUERY(A:E, "SELECT A, C, D, E WHERE B=\'offer_123\' ORDER BY A")',
      '=VLOOKUP("offer_123", A:E, {1,3,4,5}, FALSE)',
      '=SORT(FILTER(A:E, B:B="offer_123"), A, TRUE, FALSE)',
    ],
    correctIndex: 1,
    explanation:
      'QUERY handles column selection, filtering, and sorting all in one formula. SELECT A, C, D, E picks the columns, WHERE filters to the offer, and ORDER BY A sorts by date.',
  },
]

/* ─── Demo data ─── */
const COMBINED_DATA = [
  { offer: 'offer_123', offerName: 'InsureMe Now', date: '2024-01-15', clicks: 210, conv: 8, cr: '3.8' },
  { offer: 'offer_101', offerName: 'CreditCards Plus', date: '2024-01-15', clicks: 340, conv: 12, cr: '3.5' },
  { offer: 'offer_123', offerName: 'InsureMe Now', date: '2024-01-16', clicks: 185, conv: 6, cr: '3.2' },
  { offer: 'offer_101', offerName: 'CreditCards Plus', date: '2024-01-16', clicks: 290, conv: 15, cr: '5.2' },
  { offer: 'offer_123', offerName: 'InsureMe Now', date: '2024-01-17', clicks: 195, conv: 9, cr: '4.6' },
  { offer: 'offer_145', offerName: 'TravelDeals Pro', date: '2024-01-15', clicks: 410, conv: 22, cr: '5.4' },
]

const REPORT_DATA = COMBINED_DATA
  .filter((r) => r.offer === 'offer_123')
  .sort((a, b) => a.date.localeCompare(b.date))

const TOTALS = {
  clicks: REPORT_DATA.reduce((s, r) => s + r.clicks, 0),
  conv: REPORT_DATA.reduce((s, r) => s + r.conv, 0),
}
TOTALS.cr = ((TOTALS.conv / TOTALS.clicks) * 100).toFixed(1)

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Main Module ─── */
export default function Module10() {
  return (
    <ModuleLayout moduleId={12}>
      <div className="space-y-8">
        {/* Section 1: What the Client Report Looks Like */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">What the Client Report Looks Like</h2>
              <p className="text-lg text-gray-600">
                The client wants a clean table for their campaign — offer names they recognise, not internal IDs. No data from other clients.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="overflow-hidden rounded-lg border-2 border-red-300 shadow-sm"
            >
              <div className="bg-red-600 text-white px-4 py-2 flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4" />
                <span className="text-sm font-bold">Client Report — InsureMe Now</span>
              </div>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-red-50">
                    <th className="border-b border-r border-red-200 px-4 py-2 text-xs text-red-700 font-medium text-left">Date</th>
                    <th className="border-b border-r border-red-200 px-4 py-2 text-xs text-red-700 font-medium text-left">Offer</th>
                    <th className="border-b border-r border-red-200 px-4 py-2 text-xs text-red-700 font-medium text-right">Clicks</th>
                    <th className="border-b border-r border-red-200 px-4 py-2 text-xs text-red-700 font-medium text-right">Conversions</th>
                    <th className="border-b border-red-200 px-4 py-2 text-xs text-red-700 font-medium text-right">CR%</th>
                  </tr>
                </thead>
                <tbody>
                  {REPORT_DATA.map((row, i) => (
                    <tr key={i}>
                      <td className="border-b border-r border-red-100 px-4 py-1.5 text-xs">{row.date}</td>
                      <td className="border-b border-r border-red-100 px-4 py-1.5 text-xs">{row.offerName}</td>
                      <td className="border-b border-r border-red-100 px-4 py-1.5 text-xs text-right">{row.clicks.toLocaleString()}</td>
                      <td className="border-b border-r border-red-100 px-4 py-1.5 text-xs text-right">{row.conv}</td>
                      <td className="border-b border-red-100 px-4 py-1.5 text-xs text-right font-semibold">{row.cr}%</td>
                    </tr>
                  ))}
                  <tr className="bg-red-50 font-bold">
                    <td className="border-t-2 border-red-300 px-4 py-2 text-xs">Total</td>
                    <td className="border-t-2 border-red-300 px-4 py-2 text-xs" />
                    <td className="border-t-2 border-red-300 px-4 py-2 text-xs text-right">{TOTALS.clicks.toLocaleString()}</td>
                    <td className="border-t-2 border-red-300 px-4 py-2 text-xs text-right">{TOTALS.conv}</td>
                    <td className="border-t-2 border-red-300 px-4 py-2 text-xs text-right text-red-700">{TOTALS.cr}%</td>
                  </tr>
                </tbody>
              </table>
            </motion.div>

            <p className="text-sm text-gray-500 text-center">
              Clean, professional, and easy to read. Offer names the client recognises, no internal IDs, no other clients&apos; data.
            </p>
          </div>
        </Section>

        {/* Section 2: Extracting with FILTER */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Extracting with FILTER</h2>
              <p className="text-lg text-gray-600">
                Use FILTER on your combined dataset to pull out one offer's data.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-sm text-red-400 font-mono">
                =FILTER(CombinedData!A:E, CombinedData!B:B="offer_123")
              </code>
            </div>

            <div className="flex items-center gap-4 flex-wrap justify-center">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-600">
                Combined Dataset (all offers)
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400" />
              <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
                FILTER
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400" />
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                Report for offer_123
              </div>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> FILTER returns all columns. If you do not want the Offer ID column in the report (since it is the same for every row), you will need to either select specific columns manually or use QUERY instead.
              </p>
            </div>
          </div>
        </Section>

        {/* Section 3: Extracting with QUERY */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Extracting with QUERY</h2>
              <p className="text-lg text-gray-600">
                QUERY is even better here — you can SELECT only the columns you want and sort by date.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-sm text-red-400 font-mono">
                =QUERY(CombinedData!A:E, "SELECT A, C, D, E WHERE B='offer_123' ORDER BY A")
              </code>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
                <p className="text-xs font-bold text-blue-700">SELECT A, C, D, E</p>
                <p className="text-xs text-blue-600 mt-1">Skip the Offer ID column (B)</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
                <p className="text-xs font-bold text-amber-700">WHERE B='offer_123'</p>
                <p className="text-xs text-amber-600 mt-1">Only this offer's rows</p>
              </div>
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-center">
                <p className="text-xs font-bold text-green-700">ORDER BY A</p>
                <p className="text-xs text-green-600 mt-1">Sorted by date</p>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center"
            >
              <p className="text-emerald-800 font-semibold">
                One formula: filtered, sorted, and with only the columns the client needs.
              </p>
            </motion.div>
          </div>
        </Section>

        {/* Section 4: Formatting the Report */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Formatting the Report</h2>
              <p className="text-lg text-gray-600">
                A few touches to make it client-ready.
              </p>
            </div>

            <div className="space-y-3">
              {[
                { tip: 'Format CR% as percentage', detail: 'Select the column, Format > Number > Percent. The client should see "3.8%" not "0.038".' },
                { tip: 'Add a header row with clear labels', detail: 'Use "Date", "Clicks", "Conversions", "Conversion Rate" — not column letters.' },
                { tip: 'Add a totals row at the bottom', detail: 'Use SUM for Clicks and Conversions. For total CR%, divide total conversions by total clicks.' },
                { tip: 'Consider a date range filter', detail: 'Add start/end date cells that the FILTER or QUERY references, so you can easily change the reporting period.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{item.tip}</p>
                    <p className="text-xs text-gray-600 mt-1">{item.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* Section 5: Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={12} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
